import React, { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '../hooks/HPermissions';
import { useHUser } from '../hooks/HUsers';
import { LoadingSpinner } from '../../utils/LoadingSpinner';
import GObjectsTable from '../../tables/GObjectsTable';
import { Box, TextField, Button, Autocomplete } from '@mui/material';
import { API_BASE } from '../../../config/constants';

export default function PermissionsTab() {
    const { users, loadUsers } = useHUser();
    const [selectedPermissionUserId, setSelectedPermissionUserId] = useState<string>('');
    const { permissions, createPermission, removePermission } = usePermissions(
        selectedPermissionUserId
    );
    const [permissionsObjects, setPermissionsObjects] = useState<any[]>([]);
    const [permissionsObjectsLoading, setPermissionsObjectsLoading] = useState(false);
    const [selectedPermissionObjectIds, setSelectedPermissionObjectIds] = useState<Array<string | number>>([]);

    const selectableUsers = useMemo(() => users, [users]);

    const getUserDisplayName = (user: any): string => {
        const firstName = (user?.first_name ?? user?.firstName ?? '').toString().trim();
        const lastName = (user?.last_name ?? user?.lastName ?? '').toString().trim();
        const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
        const email = (user?.email ?? '').toString().trim();

        if (fullName && email) return `${fullName} (${email})`;
        if (fullName) return fullName;
        if (email) return email;
        return `User ${String(user?.id ?? '')}`;
    };

    useEffect(() => {
        void loadUsers();
    }, [loadUsers]);

    useEffect(() => {
        if (!selectedPermissionUserId && selectableUsers.length > 0) {
            const firstUser = selectableUsers[0];
            if (firstUser?.id !== undefined && firstUser?.id !== null) {
                setSelectedPermissionUserId(String(firstUser.id));
            }
        }
    }, [selectableUsers, selectedPermissionUserId]);

    const selectedPermissionUser = useMemo(
        () => users.find((u) => String(u.id) === String(selectedPermissionUserId)) ?? null,
        [users, selectedPermissionUserId]
    );

    const extractPermissionObjectIds = (permissionRows: any[]): Array<string | number> => {
        const ids = permissionRows
            .map((item) => item?.object_id ?? item?.object?.object_id ?? item?.object?.id)
            .filter((id) => id !== null && id !== undefined && String(id).trim() !== '');

        return Array.from(new Set(ids.map((id) => String(id))));
    };

    useEffect(() => {
        setSelectedPermissionObjectIds(extractPermissionObjectIds(permissions || []));
    }, [permissions]);

    const handleAssignPermissions = async () => {
        const selectedUser = users.find((user) => String(user.id) === String(selectedPermissionUserId));
        const roleId = selectedUser?.role_id ?? selectedUser?.roleId;
        const objectIdsToAssign = [...selectedPermissionObjectIds];

        if (!selectedPermissionUserId) return;
        if (roleId === null || roleId === undefined || String(roleId).trim() === '') return;
        if (objectIdsToAssign.length === 0) return;

        try {
            const removed = await removePermission(selectedPermissionUserId, null);
            if (!removed) return;

            for (const objectId of objectIdsToAssign) {
                await createPermission(selectedPermissionUserId, objectId, roleId);
            }
        } catch (err) {
            console.error('Failed to assign permissions', err);
        }
    };

    const handleTogglePermissionObject = (objectId: string | number) => {
        const normalizedId = String(objectId);
        setSelectedPermissionObjectIds((prev) => {
            const exists = prev.some((id) => String(id) === normalizedId);
            if (exists) return prev.filter((id) => String(id) !== normalizedId);
            return [...prev, normalizedId];
        });
    };

    const sortedPermissionsObjects = useMemo(() => {
        const selectedIds = new Set(selectedPermissionObjectIds.map((id) => String(id)));

        const getObjectId = (obj: any): string => {
            const id = obj?.id ?? obj?.object_id;
            return id === undefined || id === null ? '' : String(id);
        };

        const getObjectName = (obj: any): string =>
            String(obj?.name ?? obj?.object_name ?? '').trim().toLowerCase();

        return [...permissionsObjects].sort((a, b) => {
            const aSelected = selectedIds.has(getObjectId(a));
            const bSelected = selectedIds.has(getObjectId(b));

            if (aSelected !== bSelected) {
                return aSelected ? -1 : 1;
            }

            return getObjectName(a).localeCompare(getObjectName(b));
        });
    }, [permissionsObjects, selectedPermissionObjectIds]);

    useEffect(() => {
        const loadAllObjectsForPermissions = async () => {
            setPermissionsObjectsLoading(true);
            try {
                const response = await fetch(`${API_BASE}/api/objects/get-all-objects`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                });

                if (!response.ok) throw new Error('Failed to fetch all objects');

                const data = await response.json();
                const allObjects = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
                setPermissionsObjects(allObjects);
            } catch (err) {
                console.error('Failed loading objects for Permissions tab:', err);
                setPermissionsObjects([]);
            } finally {
                setPermissionsObjectsLoading(false);
            }
        };

        void loadAllObjectsForPermissions();
    }, []);

    return (
        <div className='standard-div'>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className='standard-div-flex'>
                    <h1>Permissions</h1>
                    <Box sx={{ minWidth: 320, maxWidth: 420, mb: 2 }}>
                        <Autocomplete
                            fullWidth
                            size='small'
                            options={selectableUsers}
                            value={selectedPermissionUser}
                            autoHighlight
                            openOnFocus
                            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                            getOptionLabel={getUserDisplayName}
                            onChange={(_, nextUser) => {
                                setSelectedPermissionUserId(nextUser?.id != null ? String(nextUser.id) : '');
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='User Name'
                                    placeholder='Type a name or email'
                                    helperText='Type to search users and load permissions'
                                />
                            )}
                        />
                    </Box>
                </div>

                <div className='standard-div' style={{ marginTop: 16 }}>
                    <div className='standard-div-flex'>
                        <h2>All Objects</h2>
                        <Button variant='contained' color='primary' onClick={handleAssignPermissions}>
                            Assign
                        </Button>
                    </div>
                </div>

                <div style={{ width: '100%' }}>
                    {permissionsObjectsLoading ? (
                        <LoadingSpinner text='Loading objects...' />
                    ) : (
                        <GObjectsTable
                            objects={sortedPermissionsObjects}
                            user={selectedPermissionUser}
                            selectableFlags
                            selectedObjectIds={selectedPermissionObjectIds}
                            onToggleObjectSelect={handleTogglePermissionObject}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
